import React from "react";
import {
  clearChunkLoadReloadFlag,
  tryReloadOnChunkError,
} from "./lib/chunk-load-error";

function lazyNamed(
  loader: () => Promise<Record<string, React.ComponentType<object>>>,
  exportName: string,
) {
  return React.lazy(async () => {
    try {
      const module = await loader();
      clearChunkLoadReloadFlag();
      return { default: module[exportName] };
    } catch (error) {
      if (tryReloadOnChunkError(error)) {
        // Stay pending until the page reloads so React never surfaces the error UI.
        return new Promise(() => {});
      }
      throw error;
    }
  });
}

export const RouteFallback: React.FC = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="text-center">
      <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground">Loading page...</p>
    </div>
  </div>
);

export const Dashboard = lazyNamed(() => import("./components/Dashboard"), "Dashboard");
export const BusinessAdminDashboard = lazyNamed(
  () => import("./components/BusinessAdminDashboard"),
  "BusinessAdminDashboard",
);
export const FinanceAdminDashboard = lazyNamed(
  () => import("./components/FinanceAdminDashboard"),
  "FinanceAdminDashboard",
);
export const ClientSearch = lazyNamed(
  () => import("./components/client/ClientSearch"),
  "ClientSearch",
);
export const ClientProfile = lazyNamed(
  () => import("./components/client/ClientProfile"),
  "ClientProfile",
);
export const CreatePerson = lazyNamed(
  () => import("./components/client/CreatePerson"),
  "CreatePerson",
);
export const CreateOrganisation = lazyNamed(
  () => import("./components/client/CreateOrganisation"),
  "CreateOrganisation",
);
export const QuotesPipeline = lazyNamed(
  () => import("./components/QuotesPipeline"),
  "QuotesPipeline",
);
export const CombinedQuoteBuilder = lazyNamed(
  () => import("./components/quote/combined-quote-builder/CombinedQuoteBuilder"),
  "CombinedQuoteBuilder",
);
export const AssetDetail = lazyNamed(() => import("./components/AssetDetail"), "AssetDetail");
export const RenewalsView = lazyNamed(
  () => import("./components/renewals/RenewalsView"),
  "RenewalsView",
);
export const RenewalDetail = lazyNamed(
  () => import("./components/renewals/RenewalDetail"),
  "RenewalDetail",
);
export const RenewalAmendment = lazyNamed(
  () => import("./components/renewals/RenewalAmendment"),
  "RenewalAmendment",
);
export const PoliciesView = lazyNamed(() => import("./components/PoliciesView"), "PoliciesView");
export const PolicyDetail = lazyNamed(() => import("./components/PolicyDetail"), "PolicyDetail");
export const PolicyQuoteHistory = lazyNamed(
  () => import("./components/PolicyQuoteHistory"),
  "PolicyQuoteHistory",
);
export const MTAWorkflow = lazyNamed(() => import("./components/MTAWorkflow"), "MTAWorkflow");
export const InvoicesView = lazyNamed(() => import("./components/InvoicesView"), "InvoicesView");
export const ReconciliationHistoryView = lazyNamed(
  () => import("./components/invoices/ReconciliationHistoryView"),
  "ReconciliationHistoryView",
);
export const InvoiceDetail = lazyNamed(
  () => import("./components/InvoiceDetail"),
  "InvoiceDetail",
);
export const ReportsView = lazyNamed(() => import("./components/ReportsView"), "ReportsView");
export const DebitorsReport = lazyNamed(
  () => import("./components/DebitorsReport"),
  "DebitorsReport",
);
export const CashCreditsReportView = lazyNamed(
  () => import("./components/CashCreditsReportView"),
  "CashCreditsReportView",
);
export const BusinessWrittenReportView = lazyNamed(
  () => import("./components/BusinessWrittenReportView"),
  "BusinessWrittenReportView",
);
export const PolicyRenewalReportView = lazyNamed(
  () => import("./components/PolicyRenewalReportView"),
  "PolicyRenewalReportView",
);
export const CommissionsReportView = lazyNamed(
  () => import("./components/CommissionsReportView"),
  "CommissionsReportView",
);
export const AdviserPerformanceReport = lazyNamed(
  () => import("./components/advisers/AdviserPerformanceReport"),
  "AdviserPerformanceReport",
);
export const BordeauxReport = lazyNamed(
  () => import("./components/BordeauxReport"),
  "BordeauxReport",
);
export const UsersView = lazyNamed(() => import("./components/UsersView"), "UsersView");
export const UserDetail = lazyNamed(() => import("./components/UserDetail"), "UserDetail");
export const AdviserDetailView = lazyNamed(
  () => import("./components/advisers/AdviserDetailView"),
  "AdviserDetailView",
);
export const BusinessAdminDetailView = lazyNamed(
  () => import("./components/users/BusinessAdminDetailView"),
  "BusinessAdminDetailView",
);
export const CreateFAP = lazyNamed(() => import("./components/faps/CreateFAP"), "CreateFAP");
export const EditFAP = lazyNamed(() => import("./components/faps/EditFAP"), "EditFAP");
export const FAPDetail = lazyNamed(() => import("./components/faps/FAPDetail"), "FAPDetail");
export const NotificationsPage = lazyNamed(
  () => import("./components/NotificationsPage"),
  "NotificationsPage",
);
export const HealthPage = lazyNamed(() => import("./components/HealthPage"), "HealthPage");
export const AuditLogView = lazyNamed(() => import("./components/AuditLogView"), "AuditLogView");
export const AppConfigPage = lazyNamed(
  () => import("./components/AppConfigPage"),
  "AppConfigPage",
);
export const EOfferDemo = lazyNamed(() => import("./components/EOfferDemo"), "EOfferDemo");
export const PublicEOfferPage = lazyNamed(
  () => import("./components/PublicEOfferPage"),
  "PublicEOfferPage",
);
export const PublicEOfferReturnPage = lazyNamed(
  () => import("./components/PublicEOfferReturnPage"),
  "PublicEOfferReturnPage",
);
